import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/SEO';

const CookiePolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Cookie Policy"
        description="Learn how Ixoraly uses cookies to improve your experience. Understand cookie types, purposes, and how to manage your cookie preferences."
        path="/cookie-policy"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" data-testid="cookie-policy-heading">
            Cookie Policy
          </h1>
          <p className="text-base text-gray-300">
            Last updated: February 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-base text-gray-700 mb-8">
              This Cookie Policy explains how <strong>Ixoraly</strong> ("we", "us", or "our") uses cookies and similar tracking technologies when you visit our website at ixoraly.com.
            </p>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. What Are Cookies?</h2>
              <p className="text-gray-700 mb-4">
                Cookies are small text files that are placed on your device (computer, smartphone, or tablet) when you visit a website. They help the website remember information about your visit, making it easier to use and more useful to you.
              </p>
              <p className="text-gray-700">
                Cookies can be "persistent" (remain on your device until deleted or expire) or "session" (deleted when you close your browser).
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. How We Use Cookies</h2>
              <p className="text-gray-700 mb-4">
                Ixoraly uses cookies for the following purposes:
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-navy-600 font-bold mr-2">•</span>
                  <span><strong>Essential Cookies:</strong> Required for the website to function properly, including login authentication, security, and user preferences.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-navy-600 font-bold mr-2">•</span>
                  <span><strong>Performance & Analytics:</strong> Help us understand how visitors interact with our website to improve user experience and functionality.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-navy-600 font-bold mr-2">•</span>
                  <span><strong>Functionality Cookies:</strong> Remember your preferences such as language settings and display options.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-navy-600 font-bold mr-2">•</span>
                  <span><strong>Marketing & Advertising:</strong> Track your browsing behavior to show you relevant advertisements on other websites.</span>
                </li>
              </ul>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Types of Cookies We Use</h2>
              
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Strictly Necessary Cookies</h3>
                <p className="text-sm text-gray-700">
                  These cookies are essential for the website to function and cannot be disabled. They include authentication tokens, security features, and session management.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Analytics Cookies</h3>
                <p className="text-sm text-gray-700">
                  We use Google Analytics and similar tools to collect anonymous data about website usage, including page views, time spent on site, and traffic sources.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Functional Cookies</h3>
                <p className="text-sm text-gray-700">
                  These remember your preferences (e.g., language, region) and provide enhanced, personalized features.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Marketing Cookies</h3>
                <p className="text-sm text-gray-700">
                  Used to track visitors across websites to display relevant advertisements and measure campaign effectiveness.
                </p>
              </div>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Third-Party Cookies</h2>
              <p className="text-gray-700 mb-4">
                We may use third-party services that set their own cookies. These include:
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-navy-600 font-bold mr-2">•</span>
                  <span><strong>Google Analytics:</strong> For website traffic analysis</span>
                </li>
                <li className="flex items-start">
                  <span className="text-navy-600 font-bold mr-2">•</span>
                  <span><strong>Social Media Platforms:</strong> For social sharing and integration</span>
                </li>
                <li className="flex items-start">
                  <span className="text-navy-600 font-bold mr-2">•</span>
                  <span><strong>Payment Processors:</strong> For secure payment transactions</span>
                </li>
                <li className="flex items-start">
                  <span className="text-navy-600 font-bold mr-2">•</span>
                  <span><strong>Email Marketing Tools:</strong> For newsletter subscriptions and email campaigns</span>
                </li>
              </ul>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. How to Manage Cookies</h2>
              <p className="text-gray-700 mb-4">
                You have the right to accept or reject cookies. Here's how you can manage them:
              </p>
              
              <div className="bg-navy-50 rounded-lg p-6 mb-6">
                <h3 className="font-semibold text-gray-900 mb-3">Browser Settings</h3>
                <p className="text-sm text-gray-700 mb-3">
                  Most web browsers automatically accept cookies, but you can modify your browser settings to decline cookies:
                </p>
                <ul className="text-sm text-gray-700 space-y-2 ml-4">
                  <li>• <strong>Chrome:</strong> Settings → Privacy and Security → Cookies</li>
                  <li>• <strong>Firefox:</strong> Options → Privacy & Security → Cookies</li>
                  <li>• <strong>Safari:</strong> Preferences → Privacy → Cookies</li>
                  <li>• <strong>Edge:</strong> Settings → Privacy → Cookies</li>
                </ul>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                <p className="text-sm text-gray-700">
                  <strong>⚠️ Note:</strong> Disabling cookies may affect the functionality of our website and prevent you from accessing certain features.
                </p>
              </div>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Cookie Retention Period</h2>
              <p className="text-gray-700">
                Cookie retention periods vary depending on their purpose:
              </p>
              <ul className="space-y-2 text-gray-700 mt-4">
                <li>• <strong>Session Cookies:</strong> Deleted when you close your browser</li>
                <li>• <strong>Persistent Cookies:</strong> Remain for a set period (typically 30 days to 2 years)</li>
                <li>• <strong>Analytics Cookies:</strong> Usually stored for 24 months</li>
              </ul>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Do Not Track (DNT)</h2>
              <p className="text-gray-700">
                Some browsers include a "Do Not Track" (DNT) feature. Our website does not currently respond to DNT signals, but we are committed to respecting your privacy preferences.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Updates to This Policy</h2>
              <p className="text-gray-700">
                We may update this Cookie Policy from time to time to reflect changes in our practices or legal requirements. Any updates will be posted on this page with a revised "Last Updated" date.
              </p>
            </div>

            <div className="bg-coral-50 rounded-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-2">Questions About Our Cookie Policy?</h3>
              <p className="text-sm text-gray-700 mb-3">
                If you have any questions or concerns about how we use cookies, please contact us:
              </p>
              <p className="text-sm text-gray-700">
                <strong>Email:</strong>{' '}
                <a href="mailto:privacy@ixoraly.com" className="text-coral-600 hover:text-coral-700 font-medium">
                  privacy@ixoraly.com
                </a>
              </p>
              <p className="text-sm text-gray-700 mt-2">
                <strong>Related Policies:</strong>{' '}
                <Link to="/privacy-policy" className="text-navy-600 hover:text-navy-700 font-medium">Privacy Policy</Link>
                {' | '}
                <Link to="/terms-conditions" className="text-navy-600 hover:text-navy-700 font-medium">Terms & Conditions</Link>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CookiePolicy;
